import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useReveal } from "@/lib/animations";
import { PRODUCTS } from "@/lib/data";

export default function Products() {
  const ref = useRef(null);
  useReveal(ref);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [hash]);

  return (
    <main ref={ref} data-testid="products-page" className="bg-[#F9F6F0]">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-36 md:pt-44 pb-16">
        <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
          Our products
        </p>
        <h1
          data-reveal
          className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#0B132B] max-w-4xl"
        >
          Six strategies. One goal —{" "}
          <span className="text-gradient-breeze">protect your family</span>.
        </h1>
        <p data-reveal className="mt-7 text-lg text-[#334155] font-light leading-relaxed max-w-2xl">
          Every family is different. That's why we don't push one product — we
          build the right combination from 50+ A-rated carriers, around your
          goals, your budget, and your people.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28 space-y-8">
        {PRODUCTS.map((p, i) => {
          const Icon = p.icon;
          const isNavy = p.accent === "navy";
          const featBg = isNavy ? "bg-navy-deep-gradient" : "bg-breeze-gradient";
          const featShadow = isNavy ? "shadow-[#0B132B]/35" : "shadow-[#29ABE2]/25";
          const featTextOnLight = isNavy ? "text-[#0B132B]" : "text-[#0a6fc2]";
          return (
            <article
              key={p.id}
              id={p.id}
              data-reveal
              data-testid={`product-detail-${p.id}`}
              className={`scroll-mt-28 rounded-[2rem] p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10 ${
                p.featured
                  ? `${featBg} text-white shadow-xl ${featShadow} grain relative overflow-hidden`
                  : "bg-white border border-[#0B132B]/5 shadow-sm"
              }`}
            >
              <div className="lg:col-span-7 relative z-10">
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
                    p.featured ? "bg-white/20 text-white" : "bg-[#29ABE2]/10 text-[#0a6fc2]"
                  }`}
                >
                  <Icon size={26} />
                </div>
                <p
                  className={`text-xs tracking-[0.22em] uppercase font-semibold mb-2 ${
                    p.featured ? "text-white/70" : "text-[#29ABE2]"
                  }`}
                >
                  {p.featured ? `Featured · ${p.tag}` : p.tag}
                </p>
                <h2
                  className={`font-heading font-black text-3xl sm:text-4xl tracking-tight ${
                    p.featured ? "text-white" : "text-[#0B132B]"
                  }`}
                >
                  {p.name}
                </h2>
                <p
                  className={`mt-5 font-light leading-relaxed ${
                    p.featured ? "text-white/85" : "text-[#334155]"
                  }`}
                >
                  {p.detail}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to={`/products/${p.id}`}
                    data-testid={`product-learn-more-${p.id}`}
                    className={`inline-flex items-center gap-2 rounded-full font-semibold px-7 py-3.5 transition-all duration-300 hover:scale-105 ${
                      p.featured
                        ? `bg-white ${featTextOnLight} hover:bg-[#0B132B] hover:text-white`
                        : "bg-[#0B132B] text-white hover:bg-[#29ABE2]"
                    }`}
                  >
                    Learn more about {p.tag} <ArrowRight size={16} />
                  </Link>
                  <Link
                    to={`/quote?product=${encodeURIComponent(p.name)}`}
                    data-testid={`product-quote-button-${p.id}`}
                    className={`inline-flex items-center gap-2 rounded-full font-semibold px-7 py-3.5 transition-all duration-300 hover:scale-105 border ${
                      p.featured
                        ? "border-white/40 text-white hover:bg-white/10"
                        : "border-[#0B132B]/15 text-[#0B132B] hover:border-[#29ABE2] hover:text-[#29ABE2]"
                    }`}
                  >
                    Get a quote
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5 relative z-10">
                <ul className="space-y-4">
                  {p.points.map((pt, j) => (
                    <li
                      key={j}
                      className={`flex items-start gap-3 rounded-2xl px-5 py-4 ${
                        p.featured ? "bg-white/10" : "bg-[#F9F6F0]"
                      }`}
                    >
                      <Check
                        size={18}
                        className={`mt-0.5 shrink-0 ${p.featured ? "text-white" : "text-[#29ABE2]"}`}
                      />
                      <span
                        className={`text-sm leading-relaxed ${
                          p.featured ? "text-white/90" : "text-[#334155]"
                        }`}
                      >
                        {pt}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          );
        })}
      </section>
    </main>
  );
}
